<script lang="ts" setup>
import PriceDisplay, {
  type PriceDisplayProps
} from '@/components/atom/price-display/PriceDisplay.vue'
import RedirectButton from '@/components/atom/redirect/RedirectButton.vue'
import { computed } from 'vue'
import { translate } from '@/i18n/i18n.ts'
import { LIconButton } from '@wgg.hyve/hyve-ui-library'
import { useFeatureFlagStore } from '@/stores/feature-flag.store.ts'
import SimplePriceDisplay from '@/components/atom/price-display/SimplePriceDisplay.vue'
import ErrorMessageBox from '@/components/atom/error/ErrorMessageBox.vue'
import { useUiStore } from '@/stores/ui.store.ts'
import type { ProductOfferDto } from '@/ts/types/insurance.dto.ts'
import type { RedirectBehavior } from '@/ts/types/redirect.types'

type Props = PriceDisplayProps & {
  targetUrl: string
  targetBehavior?: RedirectBehavior
  labelButton?: string
  productOffer: ProductOfferDto | undefined
}

const props = defineProps<Props>()

const featureFlagStore = useFeatureFlagStore()
const uiStore = useUiStore()

const canOpenTariffModal = computed(() => {
  return props.productOffer?.content?.advantages && props.productOffer.documents
})

const headerClasses = computed(() => {
  const styleName = 'total-area__header-wrapper'
  if (featureFlagStore.flags.get('design-v1')) {
    return `${styleName} ${styleName}--boxed-spacing`
  }
  return styleName
})

function showTariffInfoModal() {
  if (canOpenTariffModal.value) {
    uiStore.setModal(
      'YourTariffModal',
      {
        header: {
          title: translate('modal_content.your_tariff.headline')
        },
        footer: {
          closeButton: {
            label: translate('dialog.close_button_text'),
            visible: true
          }
        }
      },
      props.productOffer
    )
  }
}
</script>

<template>
  <div class="total-area linexo ocean-theme">
    <ErrorMessageBox v-show="uiStore.hasError" :message="uiStore.error!" />

    <div :class="headerClasses">
      <h3 class="total-area__header-wrapper__headline text-color">
        {{ translate('product.your_tariff') }}
      </h3>

      <LIconButton
        icon="lnx-info"
        :disabled="!canOpenTariffModal"
        :aria-label="translate('product.open_tariff_details')"
        @click="showTariffInfoModal"
      />
    </div>

    <PriceDisplay
      v-if="featureFlagStore.flags.get('design-v2')"
      :is-loading="isLoading"
      :is-valid="isValid"
      :price="price"
    />

    <SimplePriceDisplay
      v-if="featureFlagStore.flags.get('design-v1')"
      :is-loading="isLoading"
      :is-valid="isValid"
      :price="price"
    />

    <RedirectButton
      id="redirectButton"
      ref="redirectButton"
      :disabled="!isValid"
      :label="labelButton ?? translate('product.select_tariff_linexo')"
      :target-url="targetUrl"
      :targetBehavior="props.targetBehavior"
      class="total-area__button"
    />
  </div>
</template>

<style lang="scss" scoped>
@import 'total-area';
</style>
